/**
 * Shared console styles for the legacy DICOM panels (labels, predictions, viewer controls,
 * Encephalon). Same palette and rhythm as the Explorer pages: flat surfaces, thin rules,
 * small uppercase section titles, segmented tabs, left-aligned tables with row hover.
 */
import { css } from 'styled-components';

export const panel = css`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 12px;
  position: relative;
`;

export const panelHeader = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  position: static;
  background: transparent;
  padding: 0;
  margin-bottom: 10px;
`;

export const panelTitle = css`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-3);
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 4px;
`;

export const segButton = css<{ active?: boolean }>`
  font: inherit;
  font-size: 12.5px;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${p => (p.active ? 'var(--accent)' : 'var(--line)')};
  background: ${p => (p.active ? 'var(--surface-sel)' : 'var(--surface-2)')};
  color: ${p => (p.active ? 'var(--ink)' : 'var(--ink-2)')};
  font-weight: ${p => (p.active ? 600 : 400)};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--ink);
  }
`;

export const consoleTable = css`
  background: transparent !important;
  margin-top: 4px;

  .ant-table {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-size: 13px;
  }
  .ant-table-container,
  .ant-table-content {
    background: transparent !important;
  }
  .ant-table-thead > tr > th {
    background: transparent !important;
    color: var(--ink-3) !important;
    font-size: 11.5px;
    font-weight: 600;
    border-bottom: 1px solid var(--line) !important;
    border-left: 0 !important;
    text-align: left;
    padding: 6px 8px !important;
  }
  .ant-table-thead > tr > th::before {
    display: none !important;
  }
  .ant-table-tbody > tr > td {
    background: transparent !important;
    color: var(--ink) !important;
    padding: 6px 8px !important;
    text-align: left;
    border-left: 0 !important;
    border-bottom: 1px solid var(--line) !important;
    font-variant-numeric: tabular-nums;
  }
  .ant-table-tbody > tr:hover > td,
  .ant-table-tbody > tr > td.ant-table-cell-row-hover {
    background: var(--surface-2) !important;
  }
  .ant-table-tbody > tr.missing-paths-row > td {
    background: rgba(255, 93, 108, 0.1) !important;
  }
  .ant-table-tbody > tr > td.uuid-cell {
    color: var(--accent) !important;
    font-family: var(--f-mono);
    font-size: 12px;
  }
  .ant-table-placeholder td {
    color: var(--ink-3) !important;
    border-bottom: 0 !important;
  }
  .ant-empty-description {
    color: var(--ink-3);
  }
  .ant-pagination {
    margin: 8px 0 0 !important;
  }
`;

export const primaryButton = css`
  height: 32px;
  padding: 0 14px;
  background: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 6px;
  color: #0b1220;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.08);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    filter: none;
  }
`;

export const secondaryButton = css`
  height: 32px;
  padding: 0 12px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: 6px;
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    border-color: var(--accent);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
